import i18n from '@/i18n';

/**
 * Voice-related error code → localised user-facing message. Every code
 * the SFU / voice gateway can emit has a friendly translation; unknown
 * codes fall back to a generic message rather than leaking the raw
 * server error to the UI.
 *
 * These run from both React and non-React code (e.g. voiceCommands.ts), so
 * they read the active language straight off the i18n singleton (`i18n.t`)
 * rather than taking a `t` param — the message is produced on demand when an
 * error fires, so it always reflects the current language.
 *
 * Three concerns split by audience:
 *   - `humanizeVoiceJoinError`: failures during channel join attempt
 *     (capacity / permission / channel state).
 *   - `humanizeVoiceMediaError`: device/permission failures from the
 *     browser's `getUserMedia` / `getDisplayMedia` calls.
 *   - `humanizeVoiceModerationError`: voice-moderation actions failing
 *     because of role hierarchy / target state.
 */

export function humanizeVoiceJoinError(error?: string): string {
  switch (error) {
    case 'voice_channel_full':
      return i18n.t('voiceErr.join.full');
    case 'voice_join_forbidden':
      return i18n.t('voiceErr.join.forbidden');
    case 'blocked':
      return i18n.t('voiceErr.join.blocked');
    // Normally this never reaches a message: joinVoice routes it to
    // onConsentRequired so the notice is shown instead. Translated anyway, for
    // a caller that passes no such handler — a bare error beats a raw code.
    case 'broadcast_consent_required':
      return i18n.t('voiceErr.join.consentRequired');
    case 'invalid_channel':
      return i18n.t('voiceErr.join.invalidChannel');
    case 'screen_permission_denied':
      return i18n.t('voiceErr.join.screenDenied');
    case 'screen_access_failed':
    case 'screen_sfu_required':
      return i18n.t('voiceErr.join.screenFailed');
    default:
      return i18n.t('voiceErr.join.default');
  }
}

export function humanizeDmInviteError(error?: string): string {
  switch (error) {
    case 'too_fast':
      return i18n.t('voiceErr.invite.tooFast');
    case 'blocked':
      return i18n.t('voiceErr.invite.blocked');
    case 'no_one':
      return i18n.t('voiceErr.invite.noOne');
    default:
      return i18n.t('voiceErr.invite.default');
  }
}

export function humanizeVoiceModerationError(error?: string): string {
  switch (error) {
    case 'role_hierarchy_forbidden':
      return i18n.t('voiceErr.mod.hierarchy');
    case 'voice_moderation_forbidden':
      return i18n.t('voiceErr.mod.forbidden');
    case 'voice_target_not_connected':
      return i18n.t('voiceErr.mod.notConnected');
    case 'voice_channel_full':
      return i18n.t('voiceErr.mod.targetFull');
    default:
      return i18n.t('voiceErr.mod.default');
  }
}

export function humanizeVoiceMediaError(error: string): string {
  switch (error) {
    case 'microphone_permission_denied':
      return i18n.t('voiceErr.media.micDenied');
    case 'microphone_not_found':
      return i18n.t('voiceErr.media.micNotFound');
    case 'microphone_busy':
      return i18n.t('voiceErr.media.micBusy');
    case 'microphone_access_failed':
      return i18n.t('voiceErr.media.micFailed');
    case 'camera_permission_denied':
      return i18n.t('voiceErr.media.camDenied');
    case 'camera_not_found':
      return i18n.t('voiceErr.media.camNotFound');
    case 'camera_busy':
      return i18n.t('voiceErr.media.camBusy');
    case 'camera_access_failed':
      return i18n.t('voiceErr.media.camFailed');
    case 'screen_permission_denied':
      return i18n.t('voiceErr.media.screenDenied');
    case 'screen_not_found':
      return i18n.t('voiceErr.media.screenNotFound');
    case 'screen_busy':
      return i18n.t('voiceErr.media.screenBusy');
    case 'screen_access_failed':
      return i18n.t('voiceErr.media.screenFailed');
    case 'screen_sfu_required':
      return i18n.t('voiceErr.media.sfuRequired');
    case 'sfu_not_in_channel':
      return i18n.t('voiceErr.media.notInChannel');
    case 'media_not_ready':
      return i18n.t('voiceErr.media.notReady');
    default:
      return i18n.t('voiceErr.media.default');
  }
}

/**
 * Format a millisecond duration as `M:SS`. Used for voice-call elapsed
 * timers in the call status strip — independent of locale so the digit
 * width stays predictable in a 60-fps ticker.
 */
export function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
